import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'classnames';

import { Button, TextButton, ButtonProps } from '@xingternal/button';
import { BodyCopy, Headline } from '@xingternal/typography';
import { ContentSwitcher } from '@xingternal/content-switcher';
import { IconInfo } from '@xingternal/icons';
import { usePopOver } from '@xingternal/pop-over';
import { Menu } from '@xingternal/menu';

import styles from './EditHeader.css';

type EditHeaderContentSwitcherProps = {
  activeIndex: number;
  setActiveIndex: (activeIndex: number) => void;
  disablePageActions?: boolean;
  handleValidations?: any;
  contentKeyList: string[];
};

type InfoMessage = {
  key: string;
  values: any;
};

type InfoProps = {
  infoMessage: InfoMessage;
};

type EditHeaderProps = {
  titleKey: string;
  infoMessage?: InfoMessage;
  descriptionKey?: string;
  contentSwitcher?: EditHeaderContentSwitcherProps;
  cta?: ButtonProps;
  noMarginBottom?: boolean;
  userGuidanceCopy?: string;
};

const EditHeaderContentSwitcher = ({
  activeIndex,
  setActiveIndex,
  disablePageActions,
  handleValidations,
  contentKeyList,
}: EditHeaderContentSwitcherProps) => {
  const intl = useIntl();

  return (
    <div className={styles.contentSwitcher}>
      <ContentSwitcher
        activeItemIndex={activeIndex}
        variant="filled"
        data={contentKeyList.map((item, index) => ({
          copy: intl.formatMessage({
            id: item,
          }),
          onClick:
            activeIndex === index
              ? () => !disablePageActions && setActiveIndex(index)
              : () => {
                  !disablePageActions &&
                  handleValidations?.[contentKeyList[activeIndex]]
                    ? handleValidations[contentKeyList[activeIndex]](() =>
                        setActiveIndex(index)
                      )
                    : setActiveIndex(index);
                },
        }))}
      />
    </div>
  );
};

const Info = ({ infoMessage }: InfoProps) => {
  const popOver = usePopOver();
  return (
    <>
      <TextButton
        innerRef={popOver.triggerRef}
        onClick={popOver.togglePopOver}
        icon={IconInfo}
        aria-label="Info"
      />

      <Menu
        dimmerTitle="close menu"
        onOutsideClick={popOver.handleHide}
        show={popOver.show}
        triggerRef={popOver.triggerRef}
      >
        <div className={styles.menuContainer}>
          <FormattedMessage
            id={infoMessage.key}
            defaultMessage={infoMessage.key}
            values={infoMessage.values}
          />
        </div>
      </Menu>
    </>
  );
};

const EditHeader = ({
  titleKey,
  infoMessage,
  userGuidanceCopy,
  descriptionKey,
  cta,
  contentSwitcher,
  noMarginBottom,
}: EditHeaderProps) => (
  <div
    className={cx(styles.container, {
      [styles.noMarginBottom]: noMarginBottom,
    })}
  >
    <div className={styles.headlineContainer}>
      <Headline size="xxlarge" noMargin className={styles.headline}>
        <FormattedMessage id={titleKey} />
        {infoMessage && <Info infoMessage={infoMessage} />}
      </Headline>

      {cta && (
        <Button
          variant={'secondary'}
          size={'small'}
          noMargin
          className={styles.cta}
          {...cta}
        >
          {cta.children}
        </Button>
      )}
    </div>
    {userGuidanceCopy && (
      <BodyCopy size={'small'} noMargin>
        <div className={styles.sloganGuidance}>
          <FormattedMessage
            id={userGuidanceCopy}
            defaultMessage={userGuidanceCopy}
          />
        </div>
      </BodyCopy>
    )}
    {descriptionKey && (
      <BodyCopy size={'small'} noMargin className={styles.description}>
        <FormattedMessage id={descriptionKey} />
      </BodyCopy>
    )}
    {cta && (
      <Button
        variant={'secondary'}
        size={'small'}
        noMargin
        className={styles.ctaMobile}
        {...cta}
      >
        {cta.children}
      </Button>
    )}
    {contentSwitcher && <EditHeaderContentSwitcher {...contentSwitcher} />}
  </div>
);

export default EditHeader;
